import type { HTMLAttributes } from 'react';
import { avatarGradient } from '@vd/types';
import { cn } from '../cn';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  name?: string;
  src?: string;
  size?: AvatarSize;
  /** Render as an MCP bot (square, sparkle overlay). */
  bot?: boolean;
  /** Override the initial(s). Useful for MCP tokens that aren't people. */
  label?: string;
  /** Seed for gradient; defaults to `name`. */
  seed?: string;
}

function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

export function Avatar({ name, src, size = 'md', bot, label, seed, className, style, ...rest }: AvatarProps) {
  const txt = label ?? initials(name);
  const s = seed ?? name ?? txt;
  const bg = bot
    ? 'linear-gradient(135deg, var(--hl-violet), var(--hl-magenta))'
    : avatarGradient(s);

  return (
    <span
      className={cn(styles.avatar, styles[`s-${size}`], bot && styles.bot, className)}
      style={{ background: src ? undefined : bg, ...style }}
      aria-label={name}
      title={name}
      {...rest}
    >
      {src ? <img src={src} alt={name ?? ''} /> : txt}
      {bot && <span className={styles.sparkle} aria-hidden>★</span>}
    </span>
  );
}
